import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';


let socket = null;
let _userId = null;
let _userName = null;

// Pending joins: rooms to join as soon as socket connects
const pendingJoins = new Set();

// Pending messages: queued while socket is connecting
const pendingMessages = [];

/** Flush pending joins after connect */
const flushPendingJoins = () => {
  pendingJoins.forEach(({ groupId, userId, userName }) => {
    socket.emit('group:join', { groupId, userId, userName });
    console.log('[Socket] Joined room (flushed):', groupId);
  });
};

/** Flush pending messages after connect */
const flushPendingMessages = () => {
  while (pendingMessages.length > 0) {
    const payload = pendingMessages.shift();
    socket.emit('message:send', payload);
    console.log('[Socket] Sent queued message to group', payload.groupId);
  }
};

/**
 * Connect to the Socket.IO server and register the user.
 * Safe to call multiple times — always returns a working socket.
 */
export const connectSocket = (userId, userName) => {
  _userId = userId;
  _userName = userName;

  // If already connected, just return it
  if (socket && socket.connected) {
    return socket;
  }

  // If a socket exists but is broken (disconnected / errored), destroy it first
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  console.log('[Socket] Connecting to', SOCKET_URL);

  socket = io(SOCKET_URL, {
    transports: ['polling', 'websocket'], // start with polling (more reliable on many servers)
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 2000,
    reconnectionDelayMax: 10000,
    timeout: 20000,
  });

  socket.on('connect', () => {
    console.log('[Socket] Connected:', socket.id);
    socket.emit('user:join', { userId: _userId, userName: _userName });
    flushPendingJoins();
    flushPendingMessages();
  });

  socket.on('disconnect', reason => {
    console.log('[Socket] Disconnected:', reason);
  });

  socket.on('connect_error', error => {
    console.warn('[Socket] Connection error:', error.message);
  });

  return socket;
};

/** Get the current socket instance. */
export const getSocket = () => socket;

/**
 * Join a group room.
 * If the socket isn't connected yet, the join is queued and sent on connect.
 */
export const joinGroup = (groupId, userId, userName) => {
  const entry = { groupId, userId, userName };
  // Always keep in pending set so re-connects re-join automatically
  pendingJoins.add(entry);

  if (socket && socket.connected) {
    socket.emit('group:join', { groupId, userId, userName });
    console.log('[Socket] Joined room:', groupId);
  } else {
    console.log('[Socket] Queued join for room:', groupId);
  }
};

/** Leave a group room. */
export const leaveGroup = (groupId, userId, userName) => {
  if (!socket || !socket.connected) return;
  socket.emit('group:leave', { groupId, userId, userName });
};

/** Notify group members that a new member was added. */
export const emitMemberAdded = (groupId, member) => {
  if (!socket || !socket.connected) {
    console.warn('[Socket] Not connected – cannot emit group:member-added');
    return;
  }
  socket.emit('group:member-added', { groupId, member });
  console.log('[Socket] Emitted group:member-added for group', groupId);
};

/** Listen for new members being added to a group. */
export const onMemberAdded = (callback) => {
  if (!socket) return;
  socket.off('group:new-member');
  socket.on('group:new-member', callback);
};

export const offMemberAdded = () => {
  if (!socket) return;
  socket.off('group:new-member');
};

// ─── Messaging ────────────────────────────────────────────────────────────────

/**
 * Send a message to a group.
 * If the socket isn't connected yet, the message is queued and sent on connect.
 */
export const sendMessage = (payload) => {
  if (socket && socket.connected) {
    socket.emit('message:send', payload);
    console.log('[Socket] Sent message to group', payload.groupId);
  } else {
    console.log('[Socket] Queued message for group', payload.groupId);
    pendingMessages.push(payload);
    // Try to reconnect if socket doesn't exist
    if (!socket) {
      connectSocket(_userId, _userName);
    }
  }
};

/** Listen for incoming messages. */
export const onMessageReceived = (callback) => {
  if (!socket) return;
  socket.off('message:received');
  socket.on('message:received', callback);
};

export const offMessageReceived = () => {
  if (!socket) return;
  socket.off('message:received');
};

/** Emit typing:start */
export const emitTypingStart = (groupId, userId, userName) => {
  if (!socket || !socket.connected) return;
  socket.emit('typing:start', { groupId, userId, userName });
};

/** Emit typing:stop */
export const emitTypingStop = (groupId, userId, userName) => {
  if (!socket || !socket.connected) return;
  socket.emit('typing:stop', { groupId, userId, userName });
};

/** Listen for typing events. */
export const onTyping = (callback) => {
  if (!socket) return;
  socket.off('typing:user');
  socket.on('typing:user', callback);
};

export const offTyping = () => {
  if (!socket) return;
  socket.off('typing:user');
};

/** Delete a message. */
export const deleteMessage = (messageId, groupId, userId) => {
  if (!socket || !socket.connected) return;
  socket.emit('message:delete', { messageId, groupId, userId });
};

/** Listen for deleted messages. */
export const onMessageDeleted = (callback) => {
  if (!socket) return;
  socket.off('message:deleted');
  socket.on('message:deleted', callback);
};

export const offMessageDeleted = () => {
  if (!socket) return;
  socket.off('message:deleted');
};

// ─────────────────────────────────────────────────────────────────────────────

/** Disconnect and clear the socket instance. */
export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
    console.log('[Socket] Disconnected and cleared');
  }
};

// ─── Direct Chat (Guest ↔ Lawyer) ────────────────────────────────────────────

/** Join a direct chat room. Room name: direct_{guestId} */
export const joinDirectRoom = (guestId, userId, userName) => {
  if (socket && socket.connected) {
    socket.emit('direct:join', { guestId, userId, userName });
    console.log('[Socket] Joined direct room:', guestId);
  } else {
    console.log('[Socket] Not connected — queuing direct:join for room:', guestId);
    if (!socket) connectSocket(userId, userName);
  }
};

/** Leave a direct chat room. */
export const leaveDirectRoom = (guestId, userId, userName) => {
  if (!socket || !socket.connected) return;
  socket.emit('direct:leave', { guestId, userId, userName });
};

/** Send a direct message via socket (call AFTER API save). */
export const sendDirectMessage = (payload) => {
  if (socket && socket.connected) {
    socket.emit('direct:message:send', payload);
    console.log('[Socket] Sent direct message to guest room', payload.guestId);
  } else {
    console.warn('[Socket] Not connected — direct message not sent');
  }
};

/** Listen for incoming direct messages. */
export const onDirectMessageReceived = (callback) => {
  if (!socket) return;
  socket.off('direct:message:received');
  socket.on('direct:message:received', callback);
};

export const offDirectMessageReceived = () => {
  if (!socket) return;
  socket.off('direct:message:received');
};

/** Listen for direct message updates. */
export const onDirectMessageUpdated = (callback) => {
  if (!socket) return;
  socket.off('direct:message:updated');
  socket.on('direct:message:updated', callback);
};

export const offDirectMessageUpdated = () => {
  if (!socket) return;
  socket.off('direct:message:updated');
};

/** Listen for direct message deletions. */
export const onDirectMessageDeleted = (callback) => {
  if (!socket) return;
  socket.off('direct:message:deleted');
  socket.on('direct:message:deleted', callback);
};

export const offDirectMessageDeleted = () => {
  if (!socket) return;
  socket.off('direct:message:deleted');
};

/** Emit direct typing start. */
export const emitDirectTypingStart = (guestId, userId, userName) => {
  if (!socket || !socket.connected) return;
  socket.emit('direct:typing:start', { guestId, userId, userName });
};

/** Emit direct typing stop. */
export const emitDirectTypingStop = (guestId, userId, userName) => {
  if (!socket || !socket.connected) return;
  socket.emit('direct:typing:stop', { guestId, userId, userName });
};

/** Listen for direct typing indicator. */
export const onDirectTyping = (callback) => {
  if (!socket) return;
  socket.off('direct:typing:user');
  socket.on('direct:typing:user', callback);
};

export const offDirectTyping = () => {
  if (!socket) return;
  socket.off('direct:typing:user');
};

/** Delete a direct message via socket. */
export const deleteDirectMessage = (guestId, messageId, senderId) => {
  if (!socket || !socket.connected) return;
  socket.emit('direct:message:delete', { guestId, messageId, senderId });
};
